import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  CheckCircle,
  Download,
  History,
  Landmark,
  Printer,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { useAuth } from '../../contexts';
import {
  createCashClosure,
  getCashClosurePreview,
  type CashClosurePreview,
  type CashClosureRecord,
} from '../../../data/api/salesApiClient';
import { formatCurrency } from '../../lib/currency';
import { PosBackButton } from '../../components/PosBackButton';
import {
  CLOSURE_CREATED_EVENT,
  computeClosureStatus,
  formatPeriodDuration,
  MIN_FLOAT,
  TND_DENOMS,
  type CashClosureStatus,
  type CashDenomination,
} from './cloture-helpers';

const WIZARD_STEPS = ['Récapitulatif', 'Comptage', 'Extraction & Fond', 'Confirmation'];
type CountMode = 'total' | 'denominations';

function fmt(amount: number) {
  return formatCurrency(amount, 'TND');
}

function DifferenceCardDark({
  difference,
  expected,
  counted,
  status,
}: {
  difference: number;
  expected?: number;
  counted?: number;
  status: CashClosureStatus;
}) {
  const balanced = status === 'balanced';
  let border = 'border-emerald-800';
  let bg = 'bg-emerald-950/30';
  let title = 'Caisse équilibrée';
  let Icon = CheckCircle;
  let iconClass = 'text-emerald-400';
  let amountClass = 'text-emerald-400';

  if (status === 'overage' || (status === 'minor' && difference > 0)) {
    border = 'border-brand/40';
    bg = 'bg-brand/10';
    title = status === 'minor' ? 'Excédent mineur' : 'Excédent de caisse';
    Icon = TrendingUp;
    iconClass = 'text-brand';
    amountClass = 'text-brand';
  } else if (!balanced) {
    const major = status === 'major' || status === 'shortage';
    border = major ? 'border-red-800' : 'border-amber-800';
    bg = major ? 'bg-red-950/30' : 'bg-amber-950/30';
    title = status === 'major' ? 'Écart majeur' : status === 'shortage' ? 'Manque de caisse' : 'Écart mineur';
    Icon = TrendingDown;
    iconClass = major ? 'text-red-400' : 'text-amber-400';
    amountClass = iconClass;
  }

  return (
    <div className={`rounded-xl border p-4 ${bg} ${border}`}>
      <div className="flex items-start gap-3">
        <Icon size={22} className={iconClass} />
        <div className="flex-1">
          <div className="text-sm font-bold text-gray-200">{title}</div>
          <div className={`text-2xl font-bold tabular-nums mt-1 ${amountClass}`}>
            {difference >= 0 ? '+' : ''}{fmt(difference)}
          </div>
          {expected !== undefined && counted !== undefined && (
            <div className="text-xs text-gray-500 mt-2 space-y-0.5">
              <div>Attendu : <span className="text-gray-300">{fmt(expected)}</span></div>
              <div>Compté : <span className="text-gray-300">{fmt(counted)}</span></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StepBarDark({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div className="flex items-center gap-0 w-full mb-8">
      {steps.map((label, i) => {
        const num = i + 1;
        const done = num < current;
        const active = num === current;
        return (
          <div key={label} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1 shrink-0">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
                  done ? 'bg-brand border-brand text-white' : active ? 'bg-gray-900 border-brand text-brand' : 'bg-gray-900 border-gray-700 text-gray-600'
                }`}
              >
                {done ? <Check size={14} /> : num}
              </div>
              <span className={`text-[10px] font-semibold whitespace-nowrap ${active ? 'text-brand' : done ? 'text-gray-400' : 'text-gray-600'}`}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 mb-4 ${done ? 'bg-brand' : 'bg-gray-800'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function PosClotureScreen({ onBack, onComplete }: { onBack: () => void; onComplete?: () => void }) {
  const { userSession } = useAuth();
  const token = userSession?.token.accessToken ?? '';
  const branchId = userSession?.branchId ?? '';

  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState<CashClosurePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [countMode, setCountMode] = useState<CountMode>('total');
  const [countedCash, setCountedCash] = useState('');
  const [denoms, setDenoms] = useState<Record<number, number>>(() =>
    Object.fromEntries(TND_DENOMS.map((d) => [d.value, 0])),
  );
  const [extractedAmount, setExtractedAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState<CashClosureRecord | null>(null);

  const load = useCallback(() => {
    if (!token || !branchId) return;
    setLoading(true);
    getCashClosurePreview(token, branchId)
      .then(setPreview)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, branchId]);

  useEffect(() => {
    load();
  }, [load]);

  const denomTotal = useMemo(
    () => TND_DENOMS.reduce((sum, d) => sum + d.value * (denoms[d.value] ?? 0), 0),
    [denoms],
  );

  const counted = countMode === 'total' ? parseFloat(countedCash) || 0 : denomTotal;
  const expected = preview?.expectedCash ?? 0;
  const difference = counted - expected;
  const status = computeClosureStatus(difference);
  const extracted = parseFloat(extractedAmount) || 0;
  const remaining = counted - extracted;
  const floatWarning = remaining < MIN_FLOAT && remaining >= 0;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!branchId) return;
    setSaving(true);
    try {
      const denominations: CashDenomination[] | undefined =
        countMode === 'denominations'
          ? TND_DENOMS.filter((d) => (denoms[d.value] ?? 0) > 0).map((d) => ({
              label: d.label,
              value: d.value,
              qty: denoms[d.value],
            }))
          : undefined;

      const res = await createCashClosure(token, {
        branchId,
        device: 'pos',
        countedCash: counted,
        extractedAmount: extracted,
        denominations,
        managerNotes: notes.trim() || undefined,
      });
      setCreated(res);
      window.dispatchEvent(new CustomEvent(CLOSURE_CREATED_EVENT));
      onComplete?.();
      setStep(5);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-brand-dark">
        <div className="w-10 h-10 border-2 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (step === 5 && created) {
    return (
      <div className="flex-1 flex items-center justify-center bg-brand-dark p-8">
        <div className="max-w-lg w-full text-center">
          <CheckCircle size={56} className="text-emerald-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white">Clôture confirmée</h2>
          <p className="text-sm text-gray-400 font-mono mt-1">{created.visibleId}</p>
          <div className="mt-6 bg-gray-900 border border-gray-800 rounded-xl p-5 text-left text-sm space-y-2">
            <div className="flex justify-between text-gray-400"><span>Compté</span><span className="text-white tabular-nums">{fmt(created.countedCash)}</span></div>
            <div className="flex justify-between text-gray-400"><span>Écart</span><span className={`tabular-nums ${created.difference < 0 ? 'text-red-400' : created.difference > 0 ? 'text-brand' : 'text-emerald-400'}`}>{fmt(created.difference)}</span></div>
            <div className="flex justify-between text-gray-400"><span>Prélèvement</span><span className="text-white tabular-nums">{fmt(created.extractedAmount)}</span></div>
            <div className="flex justify-between text-gray-400 border-t border-gray-800 pt-2"><span>Fond restant</span><span className="text-brand tabular-nums">{fmt(created.remainingFloat)}</span></div>
          </div>
          <div className="flex flex-wrap gap-2 justify-center mt-6">
            <button type="button" className="px-3 py-2 text-xs border border-gray-700 text-gray-400 rounded-lg flex items-center gap-1"><Printer size={12} /> Imprimer</button>
            <button type="button" className="px-3 py-2 text-xs border border-gray-700 text-gray-400 rounded-lg flex items-center gap-1"><Download size={12} /> PDF</button>
            <button type="button" onClick={onBack} className="px-3 py-2 text-xs border border-gray-700 text-gray-300 rounded-lg flex items-center gap-1"><History size={12} /> Nouvelle vente</button>
          </div>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="flex-1 flex items-center justify-center text-red-400 text-sm p-8">
        {error || 'Impossible de charger le récapitulatif.'}
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-brand-dark">
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center gap-3 mb-6">
          <PosBackButton onClick={onBack} />
          <Landmark size={20} className="text-brand" />
          <div>
            <h2 className="text-lg font-bold text-white">Clôture de caisse</h2>
            <p className="text-xs text-gray-500">Étape {step} sur 4</p>
          </div>
        </div>

        <StepBarDark steps={WIZARD_STEPS} current={step} />

        {error && <div className="mb-4 text-sm text-red-400 bg-red-950/40 border border-red-900/50 rounded-lg px-3 py-2">{error}</div>}

        <form onSubmit={(e) => void handleSubmit(e)}>
          {step === 1 && (
            <div className="space-y-4">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <div className="text-xs text-gray-500 uppercase mb-2">Période de clôture</div>
                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-300">
                  <span>{new Date(preview.periodStart).toLocaleString('fr-TN')}</span>
                  <span className="text-gray-600">→</span>
                  <span>{new Date(preview.periodEnd).toLocaleString('fr-TN')}</span>
                  <span className="ml-auto text-xs text-brand font-semibold">
                    {formatPeriodDuration(preview.periodStart, preview.periodEnd)}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ['Transactions', String(preview.salesCount)],
                  ['Reçus', String(preview.receiptsCount)],
                  ['Remboursements', String(preview.refundCount)],
                  ['Fond d\'ouverture', fmt(preview.openingFloat)],
                ].map(([k, v]) => (
                  <div key={k} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                    <div className="text-[10px] text-gray-500 uppercase">{k}</div>
                    <div className="text-lg font-bold text-white tabular-nums mt-1">{v}</div>
                  </div>
                ))}
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden text-sm">
                <table className="w-full">
                  <tbody className="divide-y divide-gray-800">
                    {[
                      ['Ventes espèces', preview.cashSales],
                      ['Ventes carte', preview.cardSales],
                      ['Ventes chèque', preview.chequeSales],
                      ['Remboursements', -preview.refundsTotal],
                      ['Remises', -preview.discountsTotal],
                      ['TVA', preview.taxesTotal],
                      ['Net ventes', preview.netSales],
                      ['Total général', preview.grandTotal],
                      ['Espèces attendues', preview.expectedCash],
                    ].map(([label, val]) => (
                      <tr key={String(label)}>
                        <td className="px-4 py-2 text-gray-400">{label}</td>
                        <td className={`px-4 py-2 text-right tabular-nums font-medium ${Number(val) < 0 ? 'text-red-400' : 'text-gray-200'}`}>
                          {fmt(Number(val))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="flex gap-2">
                {(['total', 'denominations'] as CountMode[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setCountMode(m)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      countMode === m ? 'bg-brand/20 border-brand text-brand' : 'border-gray-700 text-gray-500'
                    }`}
                  >
                    {m === 'total' ? 'Total direct' : 'Par dénominations'}
                  </button>
                ))}
              </div>
              {countMode === 'total' ? (
                <div>
                  <label className="text-xs text-gray-500 uppercase font-semibold">Espèces comptées</label>
                  <input
                    type="number"
                    min={0}
                    step="0.001"
                    value={countedCash}
                    onChange={(e) => setCountedCash(e.target.value)}
                    className="w-full mt-2 text-3xl font-bold tabular-nums text-center bg-gray-900 border-2 border-gray-700 rounded-xl px-4 py-4 text-white focus:border-brand outline-none"
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {TND_DENOMS.map((d) => (
                    <div key={d.value} className="flex items-center gap-3 bg-gray-900 border border-gray-800 rounded-xl px-4 py-3">
                      <span className="text-sm text-gray-300 w-28">{d.label}</span>
                      <span className="text-gray-600">×</span>
                      <input
                        type="number"
                        min={0}
                        value={denoms[d.value] || ''}
                        onChange={(e) => setDenoms({ ...denoms, [d.value]: parseInt(e.target.value, 10) || 0 })}
                        className="w-20 text-center border border-gray-700 bg-gray-950 rounded-lg py-1.5 text-sm text-white tabular-nums"
                      />
                      <span className="ml-auto tabular-nums text-sm text-gray-400">{fmt(d.value * (denoms[d.value] ?? 0))}</span>
                    </div>
                  ))}
                  <div className="sm:col-span-2 text-right text-lg font-bold tabular-nums text-brand pt-2">
                    Total : {fmt(denomTotal)}
                  </div>
                </div>
              )}
              <DifferenceCardDark difference={difference} expected={expected} counted={counted} status={status} />
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-500 uppercase font-semibold">Montant prélevé (Prélèvement)</label>
                <input
                  type="number"
                  min={0}
                  max={counted}
                  value={extractedAmount}
                  onChange={(e) => setExtractedAmount(e.target.value)}
                  className="w-full mt-2 text-3xl font-bold tabular-nums text-center bg-gray-900 border-2 border-gray-700 rounded-xl px-4 py-4 text-white focus:border-brand outline-none"
                />
                {extracted > counted && (
                  <p className="text-xs text-red-400 mt-2">Le prélèvement ne peut pas dépasser le montant compté.</p>
                )}
              </div>
              <div className={`rounded-xl border p-4 ${remaining >= MIN_FLOAT ? 'bg-emerald-950/30 border-emerald-800' : 'bg-amber-950/30 border-amber-800'}`}>
                <div className="text-xs text-gray-500 uppercase">Fond restant en caisse</div>
                <div className="text-2xl font-bold text-brand tabular-nums">{fmt(remaining)}</div>
                <p className="text-xs text-gray-500 mt-2">Ce montant deviendra le fond d&apos;ouverture du prochain cycle.</p>
              </div>
              {floatWarning && (
                <div className="text-sm text-amber-300 bg-amber-950/40 border border-amber-800 rounded-lg p-3">
                  Le fond restant ({fmt(remaining)}) est inférieur au minimum recommandé ({fmt(MIN_FLOAT)}).
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-500 uppercase font-semibold">Notes du manager</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Observations optionnelles…"
                  className="w-full mt-1 bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 text-sm text-white"
                />
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-sm space-y-2">
                {[
                  ['Période', formatPeriodDuration(preview.periodStart, preview.periodEnd)],
                  ['Espèces attendues', fmt(expected)],
                  ['Espèces comptées', fmt(counted)],
                  ['Écart', fmt(difference)],
                  ['Prélèvement', fmt(extracted)],
                  ['Fond restant', fmt(remaining)],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between text-gray-400">
                    <span>{k}</span><span className="text-white tabular-nums">{v}</span>
                  </div>
                ))}
              </div>
              <DifferenceCardDark difference={difference} expected={expected} counted={counted} status={status} />
            </div>
          )}

          <div className="flex justify-between mt-8">
            <button type="button" disabled={step === 1} onClick={() => setStep((s) => s - 1)} className="px-4 py-2 text-sm text-gray-400 border border-gray-700 rounded-xl disabled:opacity-30">
              Précédent
            </button>
            {step < 4 ? (
              <button
                type="button"
                onClick={() => {
                  if (step === 2 && counted <= 0) {
                    setError('Veuillez saisir le montant compté.');
                    return;
                  }
                  if (step === 3 && extracted > counted) return;
                  setError('');
                  setStep((s) => s + 1);
                }}
                className="px-6 py-2 bg-brand text-white rounded-xl text-sm font-bold"
              >
                Suivant
              </button>
            ) : (
              <button type="submit" disabled={saving} className="px-6 py-2 bg-brand text-white rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-50">
                <Check size={14} /> {saving ? 'Confirmation…' : 'Confirmer la clôture'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
